import React from 'react';
import { Plot, QuizResultsHelper } from "src/stores/quiz-results-store";

export interface ResolvedGroup {
    heading: string;
    skillIds: string[];
}

export type ProgressBarProps =
    | {
        kind: 'competency';
        qr: QuizResultsHelper;
        plotkey: keyof Plot;
    }
    | {
        kind: 'skill';
        qr: QuizResultsHelper;
        plotkey: keyof Plot;
        skillId: string;
    };

export interface BarCollectionProps {
    qr: QuizResultsHelper;
    id: string;
    yOffset: number;
    title: string;
    desc: string;
    iconWidth: number;
    barHeight: number;
    margin: number;
    iconYOffset: number;
    tooltipYOffset: number;
    style: 'title' | 'subtitle';
}

/**
 * Code snippets to split a string into an array of wrapped text
 * https://medium.com/@CarysMills/wrapping-svg-text-without-svg-2-ecbfb58f7ba4
 */

function measureText(text: string, font: string) {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (context === null) {
        throw Error('Context is null');
    }
    context.font = font;
    return context.measureText(text);
}

export function getTextWidth(text: string, font: string) {
    return measureText(text, font).width;
}

export function getTextHeight(text: string, font: string) {
    return measureText(text, font).actualBoundingBoxAscent;
}

function breakString(word: string, maxWidth: number, font: string, hyphenCharacter = '-') {
    const characters = word.split('');
    const lines: string[] = [];
    let currentLine = '';
    characters.forEach((character, index) => {
        const nextLine = `${currentLine}${character}`;
        const lineWidth = getTextWidth(nextLine, font);
        if (lineWidth >= maxWidth) {
            const currentCharacter = index + 1;
            const isLastLine = characters.length === currentCharacter;
            const hyphenatedNextLine = `${nextLine}${hyphenCharacter}`;
            lines.push(isLastLine ? nextLine : hyphenatedNextLine);
            currentLine = '';
        } else {
            currentLine = nextLine;
        }
    });
    return { hyphenatedStrings: lines, remainingWord: currentLine };
}

export function wrapLabel(label: string, maxWidth: number, font: string) {
    const words = label.split(' ');
    const completedLines: string[] = [];
    let nextLine = '';
    words.forEach((word, index) => {
        const wordLength = getTextWidth(`${word} `, font);
        const nextLineLength = getTextWidth(nextLine, font);
        if (wordLength > maxWidth) {
            const { hyphenatedStrings, remainingWord } = breakString(word, maxWidth, font);
            completedLines.push(nextLine, ...hyphenatedStrings);
            nextLine = remainingWord;
        } else if (nextLineLength + wordLength >= maxWidth) {
            completedLines.push(nextLine);
            nextLine = word;
        } else {
            nextLine = [nextLine, word].filter(Boolean).join(' ');
        }
        const currentWord = index + 1;
        const isLastWord = currentWord === words.length;
        if (isLastWord) {
            completedLines.push(nextLine);
        }
    });
    return completedLines.filter(line => line !== '');
}

interface InfoIconProps {
    key: number | string;
    x?: number;
    y?: number;
    width?: number;
    height?: number;
    onMouseEnter?(): void;
    onMouseLeave?(): void;
    iconCount?: number;
}

export function infoIcon(props: InfoIconProps): JSX.Element {
    return (
        <svg
            id="Layer_1"
            key={props.key}
            x={props.x}
            y={props.y}
            width={props.width}
            height={props.height}
            data-name="Layer 1"
            viewBox="0 0 25.5 25.5"
            onMouseEnter={props.onMouseEnter}
            onMouseLeave={props.onMouseLeave}
        >
            <defs>
                <clipPath id="clip-path" >
                    <circle cx="12.75" cy="12.75" r="12" style={{ fill: 'none' }} />
                </clipPath>
            </defs>
            <g style={{ clipPath: 'url(#clip-path)' }}>
                <rect
                    x="-4.25"
                    y="-4.25"
                    width="34"
                    height="34"
                    style={{ fill: '#fff', opacity: 0 }}
                    data-tour={`infoIcon:${props.iconCount}`}
                />
            </g>
            <rect x="11.25" y="11.75" width="3" height="8" style={{ fill: '#e16e46' }} />
            <circle cx="12.75" cy="7.75" r="2" style={{ fill: '#e16e46' }} />
            <circle
                cx="12.75"
                cy="12.75"
                r="12"
                style={{
                    fill: 'none',
                    stroke: '#e16e46',
                    strokeLinejoin: 'round',
                    strokeWidth: '1.5px'
                }}
            />
        </svg>
    );
}



// WEBPACK FOOTER //
// ./src/ui/components/competency-bars/common.tsx